import React, { useState } from 'react';
import { Users, Zap, Database, MessageSquare, CheckCircle } from 'lucide-react';

/**
 * Meet Agents Tab Component
 * 
 * Introduces the different agents in the A2A ecosystem
 * Shows their roles, capabilities, and interactions
 */
const MeetAgentsTab = ({ capabilities = [] }) => {
  const [selectedAgent, setSelectedAgent] = useState('client');

  const agents = [
    {
      id: 'client',
      name: 'Client Agent (Frontend)',
      role: 'User Interface & Coordination',
      icon: <Users className="w-8 h-8" />,
      color: 'from-blue-500 to-indigo-600',
      description: 'The Client Agent is your interface to the A2A system. It runs in your web browser and coordinates all learning activities.',
      responsibilities: [
        'Display interactive learning interface',
        'Discover available capabilities from backend',
        'Send learning requests to Remote Agent',
        'Present content in an engaging, educational format',
        'Track your learning progress',
        'Provide real-time feedback and animations'
      ],
      technologies: [
        'React for UI components',
        'Zustand for state management',
        'Tailwind CSS for styling',
        'A2A Client Library for communication'
      ],
      code: `// Client Agent discovers capabilities
const client = new A2AClient(backendUrl);
const capabilities = await client.discoverCapabilities();

// Execute learning task
const result = await client.executeTask('generate_learning_content', {
  topic: 'capability_discovery',
  difficulty: 'beginner'
});`
    },
    {
      id: 'remote',
      name: 'Remote Agent (Backend)',
      role: 'AI-Powered Content Generation',
      icon: <Zap className="w-8 h-8" />,
      color: 'from-purple-500 to-pink-600',
      description: 'The Remote Agent is the brain of the system. It uses DeepSeek AI to generate educational content, simulations, and assessments.',
      responsibilities: [
        'Generate educational content using AI',
        'Create interactive simulations',
        'Assess student understanding',
        'Provide personalized feedback',
        'Manage learning workflows',
        'Secure message verification'
      ],
      technologies: [
        'FastAPI for HTTP endpoints',
        'DeepSeek AI for intelligence',
        'A2A Server Library for protocol',
        'Python for backend logic'
      ],
      capabilities: capabilities.length > 0 ? capabilities.map(cap => cap.name) : [
        'generate_learning_content',
        'simulate_a2a_interaction',
        'assess_understanding'
      ],
      code: `# Remote Agent registers capabilities
@a2a_server.register_capability(
    name="generate_learning_content",
    func=capability_generate_learning_content,
    parameters=["topic", "difficulty"]
)

# AI generates content
content = await teaching_agent.generate_content(
    topic=topic,
    difficulty=difficulty
)`
    },
    {
      id: 'database',
      name: 'Database Agent (Supabase)',
      role: 'Persistent Storage & Retrieval',
      icon: <Database className="w-8 h-8" />,
      color: 'from-green-500 to-emerald-600',
      description: 'The Database Agent stores all learning data, progress, and simulation results. It ensures your progress is never lost.',
      responsibilities: [
        'Store learning modules and content',
        'Track learner progress and performance',
        'Save simulation results',
        'Provide real-time data subscriptions',
        'Manage user sessions',
        'Generate analytics and reports'
      ],
      technologies: [
        'PostgreSQL for data storage',
        'Supabase for realtime features',
        'Row Level Security (RLS)',
        'JSONB for flexible content'
      ],
      tables: [
        'ta_learners - Student profiles',
        'ta_learning_modules - Educational content',
        'ta_learning_progress - Progress tracking',
        'ta_simulations - Simulation results'
      ],
      code: `-- Store learning progress
INSERT INTO ta_learning_progress (
  learner_id, module_id, status, progress_percent
) VALUES (
  'user-123', 'mod-456', 'in_progress', 45
);

-- Retrieve learner dashboard
SELECT * FROM learner_dashboard_view 
WHERE learner_id = 'user-123';`
    },
    {
      id: 'ai',
      name: 'AI Agent (DeepSeek)',
      role: 'Intelligence & Decision Making',
      icon: <MessageSquare className="w-8 h-8" />,
      color: 'from-orange-500 to-red-600',
      description: 'The AI Agent powers the intelligence behind content generation, personalization, and adaptive learning experiences.',
      responsibilities: [
        'Generate educational explanations',
        'Create realistic simulations',
        'Provide personalized feedback',
        'Recommend next learning steps',
        'Assess comprehension levels',
        'Adapt content to learning style'
      ],
      technologies: [
        'DeepSeek AI API',
        'Natural Language Processing',
        'Structured JSON generation',
        'Context-aware responses'
      ],
      capabilities: [
        'Explain complex concepts simply',
        'Generate code examples',
        'Create quiz questions',
        'Provide hints and guidance',
        'Simulate agent conversations'
      ],
      code: `# AI generates educational content
response = await deepseek.chat_completion([
  {
    "role": "system",
    "content": "You are an expert teacher..."
  },
  {
    "role": "user", 
    "content": f"Explain {topic} for {difficulty} level"
  }
])

content = response['choices'][0]['message']['content']`
    }
  ];

  const currentAgent = agents.find(a => a.id === selectedAgent);

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Agent Selection Sidebar */}
      <div className="col-span-3">
        <div className="card p-4 sticky top-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4">The A2A Team</h2>
          <div className="space-y-2">
            {agents.map((agent) => {
              const isActive = selectedAgent === agent.id;
              return (
                <button
                  key={agent.id}
                  onClick={() => setSelectedAgent(agent.id)}
                  className={`w-full text-left p-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-indigo-50 border-2 border-indigo-600'
                      : 'bg-white border border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${agent.color} text-white`}>
                      {agent.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-gray-900 truncate">
                        {agent.name.split(' ')[0]}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {agent.role}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Communication Flow */}
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-sm text-gray-900 mb-3">Message Flow</h3>
            <div className="space-y-2 text-xs text-gray-600">
              <div className="flex items-center space-x-2">
                <span className="text-blue-600">👤</span>
                <span>→</span>
                <span className="text-blue-600">Client</span>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <span>→</span>
                <span className="text-purple-600">Remote</span>
              </div>
              <div className="flex items-center space-x-2 ml-8">
                <span>→</span>
                <span className="text-orange-600">AI</span>
              </div>
              <div className="flex items-center space-x-2 ml-12">
                <span>→</span>
                <span className="text-green-600">Database</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Agent Details */}
      <div className="col-span-9">
        <div className="card p-8 animate-fadeIn">
          {/* Agent Header */}
          <div className="flex items-start space-x-4 mb-6">
            <div className={`p-4 rounded-2xl bg-gradient-to-br ${currentAgent.color} text-white`}>
              {currentAgent.icon}
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">
                {currentAgent.name}
              </h1>
              <p className="text-lg text-gray-600 mt-1">{currentAgent.role}</p>
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <p className="text-gray-700 leading-relaxed text-lg">
              {currentAgent.description}
            </p>
          </div>

          {/* Responsibilities */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Key Responsibilities</h2>
            <div className="grid grid-cols-2 gap-3">
              {currentAgent.responsibilities.map((resp, idx) => (
                <div 
                  key={idx} 
                  className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg animate-slideIn"
                  style={{animationDelay: `${idx * 50}ms`}}
                >
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-gray-700">{resp}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Technologies */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Technologies Used</h2>
            <div className="flex flex-wrap gap-2">
              {currentAgent.technologies.map((tech, idx) => (
                <span 
                  key={idx} 
                  className="badge bg-indigo-100 text-indigo-800 px-3 py-1.5 text-sm animate-fadeIn"
                  style={{animationDelay: `${idx * 100}ms`}}
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Capabilities (for Remote Agent) */}
          {currentAgent.capabilities && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Available Capabilities</h2>
              <div className="grid grid-cols-2 gap-3">
                {currentAgent.capabilities.map((cap, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center space-x-3 p-3 bg-purple-50 border border-purple-200 rounded-lg"
                  >
                    <Zap className="w-5 h-5 text-purple-600" />
                    <span className="text-sm font-medium text-gray-900">{cap}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Database Tables */}
          {currentAgent.tables && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Database Tables</h2>
              <div className="space-y-2">
                {currentAgent.tables.map((table, idx) => (
                  <div key={idx} className="flex items-center space-x-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <Database className="w-5 h-5 text-green-600" />
                    <span className="text-sm text-gray-700">{table}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Code Example */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Code Example</h2>
            <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
              <pre className="text-sm text-gray-100 font-mono whitespace-pre">
                {currentAgent.code}
              </pre>
            </div>
          </div>

          {/* Interaction Note */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
            <h3 className="font-bold text-gray-900 mb-2">💡 How They Work Together</h3>
            <p className="text-gray-700 text-sm">
              All four agents collaborate seamlessly. The <strong>Client Agent</strong> coordinates user interactions,
              the <strong>Remote Agent</strong> processes requests with AI, the <strong>Database Agent</strong> persists
              all data, and the <strong>AI Agent</strong> provides intelligence. This distributed architecture ensures
              scalability, maintainability, and exceptional user experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetAgentsTab;

